import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const EXAMPLES = [
  {
    id: "interview",
    before: "Help me prepare for tomorrow\u2019s job interview.",
    after:
      "I\u2019m interviewing for a Sales Manager position. Give me 10 likely interview questions, strong example answers, and three thoughtful questions to ask the interviewer.",
  },
  {
    id: "email",
    before: "Write a professional email.",
    after:
      "Write a professional follow-up email thanking a client for today\u2019s meeting, summarizing the next steps, and keeping the tone confident and friendly.",
  },
  {
    id: "salary",
    before: "Help me negotiate my salary.",
    after:
      "I\u2019ve been offered a new position at $85,000. Based on my experience and the market, help me negotiate for $95,000 professionally.",
  },
  {
    id: "meeting",
    before: "Summarize this meeting.",
    after:
      "Summarize these meeting notes into key decisions, action items, owners, and deadlines.",
  },
  {
    id: "marketing",
    before: "Create a marketing plan.",
    after:
      "Create a 30-day marketing plan for a local HVAC company with a $2,000 monthly advertising budget.",
  },
  {
    id: "business",
    before: "Help me start a business.",
    after:
      "I want to start a residential pressure washing business in Houston. Create a step-by-step launch plan, startup budget, pricing strategy, and first-month marketing plan.",
  },
];

const ROTATE_MS = 7000;

export default function ExampleCard() {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * EXAMPLES.length));

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % EXAMPLES.length), ROTATE_MS);
    return () => clearInterval(t);
  }, []);

  const ex = EXAMPLES[index];

  return (
    <section
      data-testid="example-card"
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-2 sm:mt-4"
    >
      <div className="text-center mb-5">
        <h2 className="font-display text-[1.55rem] sm:text-3xl lg:text-4xl tracking-[-0.028em] leading-[1.08]" data-testid="example-card-headline">
          See What Makes a Prompt Better
        </h2>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={ex.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4"
          data-testid={`example-card-pair-${ex.id}`}
        >
          {/* ─── BEFORE ─── */}
          <div
            data-testid="example-card-before"
            className="rounded-2xl p-5 sm:p-6"
            style={{
              background: "rgba(255, 92, 42, 0.04)",
              border: "1px solid rgba(255, 92, 42, 0.25)",
            }}
          >
            <span className="pill uppercase tracking-widest text-[10px] font-bold inline-block mb-3"
              style={{
                background: "rgba(255, 92, 42, 0.10)",
                color: "var(--accent, #E85D33)",
                borderColor: "rgba(255, 92, 42, 0.30)",
              }}
            >
              Before
            </span>
            <blockquote
              className="text-sm sm:text-base leading-relaxed font-mono"
              style={{ color: "var(--ink-1)" }}
              data-testid="example-card-before-prompt"
            >
              &ldquo;{ex.before}&rdquo;
            </blockquote>
            <p className="mt-3 text-xs sm:text-sm text-[var(--ink-3)] leading-relaxed" data-testid="example-card-before-result">
              AI guesses the tone, length, and details — you still rewrite it.
            </p>
          </div>

          {/* ─── AFTER ─── */}
          <div
            data-testid="example-card-after"
            className="rounded-2xl p-5 sm:p-6"
            style={{
              background: "rgba(14, 159, 110, 0.04)",
              border: "1px solid rgba(14, 159, 110, 0.30)",
            }}
          >
            <span className="pill uppercase tracking-widest text-[10px] font-bold inline-block mb-3"
              style={{
                background: "rgba(14, 159, 110, 0.10)",
                color: "var(--emerald, #0E9F6E)",
                borderColor: "rgba(14, 159, 110, 0.35)",
              }}
            >
              After
            </span>
            <blockquote
              className="text-sm sm:text-base leading-relaxed font-mono"
              style={{ color: "var(--ink-1)" }}
              data-testid="example-card-after-prompt"
            >
              &ldquo;{ex.after}&rdquo;
            </blockquote>
            <p className="mt-3 text-xs sm:text-sm text-[var(--ink-3)] leading-relaxed" data-testid="example-card-after-result">
              AI gets the goal, tone, format, and context.
            </p>
          </div>
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
